import { Component, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgClass } from '@angular/common';
import { Subscription, filter } from 'rxjs';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { SIDEBAR_MENU, SidebarMenuItem, SidebarMenuSection } from './config/sidebar-menu.config';
import { AccessKey } from './config/access-control.config';
import { stripAppBase } from 'src/app/app-paths';

interface NavigableMenuEntry {
  item: SidebarMenuItem;
  ancestorDropdownKeys: string[];
}

@Component({
  selector: '[main-sidebar]',
  imports: [RouterLink, CommonModule, NgClass],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  activeDropdowns = new Set<string>();
  activeSection: string | null = null;
  menuSections: SidebarMenuSection[] = SIDEBAR_MENU;
  currentUserId: number | null = null;
  logoUrl: string = '/assets/logo/logo_gateway.png';
  private allowedAccessKeys = new Set<AccessKey>();
  private subscription = new Subscription();
  private dropdownDescendants = new Map<string, string[]>();
  private navigableEntries: NavigableMenuEntry[] = [];
  private activeItem: SidebarMenuItem | null = null;

  private sectionMapping = {
    'HOME': ['/', 'home'],
    'CLIENTS': ['clients'],
    'REQUESTS': ['requests'],
    'PROPOSALS': ['proposals'],
    'PROJECTS': ['projects'],
    'USERS': ['general-settings/users'],
    'TECH': ['tech'],
    'SHARED RESOURCES': ['shared-resources'],
    'IT': ['it'],
    'COMPANY OPERATION': ['company-operation'],
    'COMPLIANCE': ['compliance'],
    'SALES': ['sales'],
    'MANAGEMENT': ['management'],
    'INTRODUCER': ['introducer'],
    'FINANCE': ['finance'],
    'HR': ['hr'],
    'QUALITY MANAGEMENT': ['quality-management'],
    'MARKETING': ['marketing'],
    'OFFICE OPERATION': ['office-operation'],
    'GENERAL SETTINGS': ['general-settings']
  };

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
  }
  ngOnInit(): void {
    this.buildDropdownDescendants();
    this.buildNavigableEntries();

    const SELECTOR_SIDEBAR_WRAPPER = '.sidebar-wrapper';
    const Default = {
      scrollbarTheme: 'os-theme-light',
      scrollbarAutoHide: 'leave',
      scrollbarClickScroll: true,
    };

    const sidebarWrapper = document.querySelector(SELECTOR_SIDEBAR_WRAPPER);
    if (sidebarWrapper && typeof window.OverlayScrollbarsGlobal?.OverlayScrollbars !== 'undefined') {
      window.OverlayScrollbarsGlobal.OverlayScrollbars(sidebarWrapper, {
        scrollbars: {
          theme: Default.scrollbarTheme,
          autoHide: Default.scrollbarAutoHide,
          clickScroll: Default.scrollbarClickScroll,
        },
      });
    }

    this.syncMenuStateWithUrl();

    this.subscription.add(
      this.mainLayoutService.logoUrl$.subscribe(url => {
        if (url && url !== this.logoUrl) {
          const img = new Image();
          img.onload = () => this.ngZone.run(() => { this.logoUrl = url; this.cdr.detectChanges(); });
          img.onerror = () => { };
          img.src = url;
        }
      })
    );

    this.subscription.add(
      this.mainLayoutService.activeProfile$.subscribe((profileCode) => {
        this.allowedAccessKeys = this.accessControlService.getAllowedKeys(profileCode);
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.mainLayoutService.user$.subscribe((user) => {
        this.currentUserId = user?.userId ?? null;
        this.syncMenuStateWithUrl();
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        this.syncMenuStateWithUrl();
        this.closeMobileSidebar();
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  private updateActiveSection(): void {
    const currentPath = stripAppBase(this.router.url);

    if (currentPath === '/') {
      this.activeSection = 'HOME';
      return;
    }

    this.activeSection =
      this.findSection((path) => currentPath.startsWith('/' + path)) ??
      this.findSection((path) => currentPath.includes('/' + path));
  }

  private findSection(matches: (path: string) => boolean): string | null {
    for (const [section, paths] of Object.entries(this.sectionMapping)) {
      if (paths.some((path) => path !== '/' && matches(path))) {
        return section;
      }
    }
    return null;
  }
  
  toggleDropdown(dropdownName: string | null | undefined, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }

    if (!dropdownName) {
      return;
    }

    if (this.activeDropdowns.has(dropdownName)) {
      this.activeDropdowns.delete(dropdownName);
      this.closeDescendantDropdowns(dropdownName);
    } else {
      this.activeDropdowns.add(dropdownName);
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      const activeElement = document.querySelector('.menu-open');
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }

  isLinkActive(item: SidebarMenuItem): boolean {
    return this.activeItem === item;
  }

  isActiveSectionHeader(sectionName: string): boolean {
    return this.activeSection === sectionName;
  }

  isDropdownOpen(key: string | null | undefined): boolean {
    return !!key && this.activeDropdowns.has(key);
  }

  isItemVisible(item: SidebarMenuItem): boolean {
    return this.allowedAccessKeys.has(item.accessKey);
  }

  isSectionVisible(section: SidebarMenuSection): boolean {
    return section.items.some((item) => this.isItemTreeVisible(item));
  }

  isItemTreeVisible(item: SidebarMenuItem): boolean {
    if (!this.isItemVisible(item)) {
      return false;
    }

    if (!item.children?.length) {
      return true;
    }

    return item.children.some((child) => this.isItemTreeVisible(child));
  }

  getRouterLink(item: SidebarMenuItem): string {
    if (item.dynamicRouterLink) {
      return item.dynamicRouterLink(this.currentUserId);
    }
    return item.routerLink ?? '/';
  }

  private syncMenuStateWithUrl(): void {
    this.updateActiveSection();

    const match = this.findActiveEntry();
    this.activeItem = match?.item ?? null;
    this.activeDropdowns = new Set(match?.ancestorDropdownKeys ?? []);
  }

  private findActiveEntry(): NavigableMenuEntry | null {
    // Confronto senza il prefisso /app: la Dashboard ('/app' → '/') deve
    // corrispondere solo all'URL esatto, non fare da prefisso a ogni pagina.
    const currentPath = stripAppBase(this.stripUrlSuffix(this.router.url));
    let best: NavigableMenuEntry | null = null;
    let bestLength = -1;

    for (const entry of this.navigableEntries) {
      const link = stripAppBase(this.getRouterLink(entry.item));
      const matches = link === '/'
        ? currentPath === '/'
        : currentPath === link || currentPath.startsWith(link + '/');

      if (matches && link.length > bestLength) {
        best = entry;
        bestLength = link.length;
      }
    }

    return best;
  }

  private stripUrlSuffix(url: string): string {
    const cut = Math.min(
      ...[url.indexOf('?'), url.indexOf('#')].filter((index) => index !== -1),
      url.length
    );
    const path = url.slice(0, cut);
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }

  private buildNavigableEntries(): void {
    this.navigableEntries = [];

    const walk = (item: SidebarMenuItem, ancestorDropdownKeys: string[]): void => {
      if (item.routerLink || item.dynamicRouterLink) {
        this.navigableEntries.push({ item, ancestorDropdownKeys });
      }

      if (!item.children?.length) {
        return;
      }

      const childAncestors = item.dropdownKey
        ? [...ancestorDropdownKeys, item.dropdownKey]
        : ancestorDropdownKeys;

      item.children.forEach((child) => walk(child, childAncestors));
    };

    this.menuSections.forEach((section) => section.items.forEach((item) => walk(item, [])));
  }

  private buildDropdownDescendants(): void {
    const collect = (item: SidebarMenuItem): void => {
      if (item.dropdownKey && item.children?.length) {
        const descendants: string[] = [];
        const walk = (child: SidebarMenuItem) => {
          if (child.dropdownKey) {
            descendants.push(child.dropdownKey);
          }
          child.children?.forEach(walk);
        };
        item.children.forEach(walk);
        this.dropdownDescendants.set(item.dropdownKey, descendants);
      }
      item.children?.forEach(collect);
    };

    this.menuSections.forEach((section) => section.items.forEach(collect));
  }

  private closeDescendantDropdowns(parentKey: string): void {
    const descendants = this.dropdownDescendants.get(parentKey) ?? [];
    descendants.forEach((key) => this.activeDropdowns.delete(key));
  }

  private closeMobileSidebar(): void {
    if (window.innerWidth <= 992) {
      document.body.classList.remove('sidebar-open');
    }
  }

  ////////********* FUNZIONE TEMPORANEA  ********////////
  redirectToLogin() {
    localStorage.clear(); 
    this.router.navigate(['/login']).then(() => {
      location.reload(); 
    });
  }
}
